import type { Task } from '@nexo/types';
import { AvatarStack, cn } from '@nexo/ui';
import { memo } from 'react';
import { useTeam } from '../../layouts/TeamContext';
import { PointsBadge, PriorityIcon, StatusButton, TaskKey, TypeIcon } from './TaskIcons';
import { ChannelBadge, DueBadge, ChecklistBadges } from './TaskMeta';
import { useUpdateTask } from './useTaskMutations';

/** Task row: one dense line in lists; title over details in the narrow conversation panel. */
export const TaskRow = memo(function TaskRow({
  task,
  showChannel = true,
  compact = false,
}: {
  task: Task;
  showChannel?: boolean;
  compact?: boolean;
}) {
  const { openTask } = useTeam();
  const update = useUpdateTask();
  const done = task.status === 'done';
  const hasDetails =
    (!done && task.dueDate !== null) || task.checklist.length > 0 || task.storyPoints !== null;
  const details = hasDetails ? (
    <>
      {!done && <DueBadge task={task} />}
      <ChecklistBadges task={task} />
      <PointsBadge points={task.storyPoints} />
    </>
  ) : null;

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={() => openTask({ mode: 'edit', taskId: task.id })}
      onKeyDown={(event) => {
        if (event.key === 'Enter') openTask({ mode: 'edit', taskId: task.id });
      }}
      className={cn(
        'group flex cursor-pointer gap-3 rounded-lg px-2.5 transition-colors hover:bg-surface-2 focus-visible:bg-surface-2',
        compact ? 'min-h-9 items-start py-2' : 'min-h-10 items-center py-2',
      )}
    >
      <StatusButton
        status={task.status}
        onChange={(status) => update.mutate({ id: task.id, input: { status } })}
      />
      <span className="flex w-4 shrink-0 justify-center">
        <PriorityIcon priority={task.priority} />
      </span>
      {!compact && (
        <span className="flex shrink-0 items-center gap-1.5 max-sm:hidden">
          <TypeIcon type={task.type} />
          <TaskKey task={task} className="min-w-12" />
        </span>
      )}
      {compact ? (
        // Narrow panel: the title gets the whole line and the details go underneath.
        <span className="flex min-w-0 flex-1 flex-col gap-1">
          <span
            className={cn(
              'line-clamp-2 text-sm',
              done && 'text-subtle line-through decoration-subtle/60',
            )}
          >
            {task.title}
          </span>
          {(details || task.assignees.length > 0) && (
            <span className="flex flex-wrap items-center gap-1.5">
              {details}
              {task.assignees.length > 0 && (
                <span className="ml-auto">
                  <AvatarStack people={task.assignees} max={2} />
                </span>
              )}
            </span>
          )}
        </span>
      ) : (
        <>
          <span
            className={cn(
              'min-w-0 flex-1 truncate text-sm',
              done && 'text-subtle line-through decoration-subtle/60',
            )}
          >
            {task.title}
          </span>
          <span className="flex shrink-0 items-center gap-1.5">
            {showChannel && (
              <span className="max-sm:hidden">
                <ChannelBadge channelId={task.channelId} />
              </span>
            )}
            {details}
          </span>
          {task.assignees.length > 0 && <AvatarStack people={task.assignees} max={3} />}
        </>
      )}
    </div>
  );
});
